import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import "./SessionCheckIn.css";

const SessionCheckIn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [message, setMessage] = useState("Checking you in...");
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const functions = getFunctions();

  // Wait for auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!authChecked) return; // Wait for auth check

    const checkIn = async () => {
      const sessionId = searchParams.get("sessionId");
      const meetLink = searchParams.get("meetLink");

      if (!sessionId || !meetLink) {
        setStatus("error");
        setMessage("Missing session information. Redirecting...");
        setTimeout(() => {
          window.location.href = "/learner-dashboard";
        }, 2000);
        return;
      }

      if (!user || !user.email) {
        setStatus("error");
        setMessage("Please log in to check in. Redirecting to login...");
        setTimeout(() => {
          window.location.href = "/learner-dashboard";
        }, 2000);
        return;
      }

      try {
      console.log("[SessionCheckIn] Attempting check-in:", {
        sessionId,
        studentEmail: user.email,
        meetLink: meetLink ? meetLink.substring(0, 50) + "..." : "N/A",
      });

      // Validate sessionId format
      if (!sessionId || sessionId.trim() === "") {
        throw new Error("Invalid session ID");
      }

        // Call the check-in function with proper error handling
        const checkInToSession = httpsCallable(functions, "checkInToSession");
        
        // Add timeout to the function call
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error("Function call timed out")), 30000);
        });
        
        const result = await Promise.race([
          checkInToSession({
            sessionId: sessionId.trim(),
            studentEmail: user.email,
            status: "present",
          }),
          timeoutPromise,
        ]);

        console.log("[SessionCheckIn] Check-in result:", result.data);

        if (result.data?.success) {
          setStatus("success");
          setMessage("Check-in successful! Redirecting to meeting...");
          
          // Redirect to Google Meet after a short delay
          setTimeout(() => {
            window.location.href = meetLink;
          }, 1500);
        } else {
          // Silently log but don't show error to user
          console.error("[SessionCheckIn] Check-in failed:", result.data);
          setStatus("success");
          setMessage("Redirecting to meeting...");
          
          // Redirect immediately without delay
          setTimeout(() => {
            window.location.href = meetLink;
          }, 500);
        }
      } catch (error) {
        // Silently log the error but don't show it to the user
        console.error("[SessionCheckIn] Check-in error:", {
          code: error.code,
          message: error.message,
          details: error.details,
        });
        
        // Redirect immediately without showing error
        setStatus("success");
        setMessage("Redirecting to meeting...");
        
        // Redirect immediately without delay
        setTimeout(() => {
          window.location.href = meetLink;
        }, 500);
      }
    };

    checkIn();
  }, [searchParams, functions, user, authChecked]);

  return (
    <div className="check-in-container">
      <div className="check-in-card">
        {status === "checking" && (
          <>
            <div className="spinner"></div>
            <h2>Checking you in...</h2>
            <p>{message}</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="success-icon">✓</div>
            <h2>Check-in Successful!</h2>
            <p>{message}</p>
          </>
        )}
        {status === "error" && (
          <>
            <div className="error-icon">⚠</div>
            <h2>Check-in Issue</h2>
            <p>{message}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default SessionCheckIn;

